// MACHINERY V2 (Stroje) — V4 shop-floor wall: tile surface lookup map (M2.14).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.14 + desktop/README.md §V4.
// Rendered truth: desktop/screenshots/v4-wall--refined-claude.png and
// v4-wall--machineid-native.png (both viewed before any of this was written).
//
// >>> WHY THIS IS A LOOKUP MAP AND NOT A STYLESHEET. <<< The manager's
// token/atom reconciliation ruling for the views wave: V4's status band
// surfaces "land as inline lookup maps (`wallTileStyles`) beside
// cardStateStyles.ts, NOT CSS classes (hover-free surfaces need no
// stylesheet)". The wall's TILES have no hover, focus or active state to
// express, so the only thing a class buys them is a second place for a colour
// to live. Same closed-enum-map idiom as STATUS_SPECS (cardStates.ts) and the
// builders in cardStateStyles.ts.
//
// (The header's exit X, added by owner ruling 2026-08-24, DOES have hover and
// focus — which is exactly why that one control reuses the shipped
// `.btn btn-ghost` classes instead of a map like this one. A `:hover` rule
// cannot live in an inline style; see the token sheet's own note at
// machinery-v2-tokens.scss:162-164. It changes nothing here.)
//
// >>> KEYED ON THE CONTRACT, NOT ON RAW TELEMETRY. <<< WALL_TILE_SPECS is a
// `Record<MachineryV2CardState, …>`, so the compiler forces an entry for all
// nine canonical states and a tile can never fall through to an untinted
// surface. The foreground ink comes from the SHIPPED statusColors() in
// cardStateStyles.ts keyed on `presentation.badgeStatus`, so the big status
// numerals can never disagree with the badge printed above them.
//
// PALETTE DELTA IS ZERO (reconciliation ruling): every var below is already in
// machinery-v2-tokens.scss, and both palettes resolve them under
// `.machinery-v2-root`. Nothing here adds a colour.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import type { CSSProperties } from "react";

import {
  MachineryV2CardPresentation,
  MachineryV2CardState,
  MachineryV2StatusToken,
} from "../cardStates";
import { statusColors } from "../cardStateStyles";

export interface WallTileSpec {
  token: MachineryV2StatusToken;
  /** Fill = `color-mix(in oklab, var(token) fill%, var(--card))`. */
  fill: number;
  /** Border = `color-mix(in oklab, var(token) border%, transparent)`. */
  border: number;
}

/**
 * The README's "tile fill saturates with status" table, 16–28%, verbatim from
 * the prototype's `tokenMap` — plus the three states the 5-state prototype does
 * not have and our 9-state contract does:
 *
 *   awaiting     — a connection row exists but nothing has ever been reported.
 *                  Neutral 10%: louder than `unconnected` (something IS there)
 *                  and quieter than any status (nothing has been said yet).
 *   stale        — connected, feed dead, status unknown. Neutral 10% for the
 *                  same reason: tinting it green because the machine was
 *                  running two hours ago is exactly the lie the wall must not
 *                  tell across a hall.
 *   stale-alarm  — an alarm is an alarm. Same red 28% as `alarm`; the tile then
 *                  carries `opacity: 0.78` from the stale look, which is the
 *                  "last known" signal, and the badge desaturates with it.
 *
 * Borders: 60% mix for every connected state, 30% for the two that are not
 * (README: "border 60% mix; unconnected = 6% neutral").
 */
export const WALL_TILE_SPECS: Record<MachineryV2CardState, WallTileSpec> = {
  unconnected: { token: "--faint", fill: 6, border: 30 },
  awaiting: { token: "--faint", fill: 10, border: 30 },
  running: { token: "--green", fill: 22, border: 60 },
  idle: { token: "--amber", fill: 22, border: 60 },
  stopped: { token: "--muted", fill: 18, border: 60 },
  alarm: { token: "--red", fill: 28, border: 60 },
  "stale-alarm": { token: "--red", fill: 28, border: 60 },
  stale: { token: "--faint", fill: 10, border: 60 },
  offline: { token: "--faint", fill: 16, border: 60 },
};

const mix = (token: MachineryV2StatusToken, pct: number, base: string): string =>
  "color-mix(in oklab, var(" + token + ") " + pct + "%, " + base + ")";

/**
 * The tile shell. `minHeight: 280`, `borderRadius: 14`, `gap: 10` and the
 * `18px 20px 16px` padding are the artboard's, unchanged.
 *
 * NO transition, NO hover, NO cursor: it is a TV. `position: relative` exists
 * only so the connectivity dot can pin to the corner — not for an overlay.
 */
export const wallTileStyle = (p: MachineryV2CardPresentation): CSSProperties => {
  const spec = WALL_TILE_SPECS[p.state];
  return {
    position: "relative",
    overflow: "hidden",
    background: mix(spec.token, spec.fill, "var(--card)"),
    border: "1px solid " + mix(spec.token, spec.border, "transparent"),
    borderRadius: 14,
    padding: "18px 20px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    minHeight: 280,
    // The one place the stale look is applied on this surface — taken from the
    // contract, never re-tested from `conn`. `offline` is deliberately NOT
    // stale-looking (see MachineryV2CardPresentation.staleLook).
    opacity: p.staleLook ? 0.78 : 1,
  };
};

/**
 * The ink for the tile's BIG status-coloured numerals (elapsed, SR count).
 *
 * The prototype mixes the status token 78% toward --ink; the shipped
 * statusColors() mixes 82%. We take the SHIPPED one — the 4-point difference is
 * invisible at wall distance, and routing this through the same function every
 * badge already uses is what makes "one status contract" true for colour and
 * not just for state. Falls back to --ink-2 when there is no status to paint
 * (unconnected / awaiting / status-less stale), which is also when those
 * numerals have nothing to say.
 */
export const wallStatusInk = (p: MachineryV2CardPresentation): string =>
  p.badgeStatus ? statusColors(p.badgeStatus, p.staleLook).fg : "var(--ink-2)";
